// Components==============
import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useContext, useState } from 'react';
import { framerFade, interactiveScale } from 'utilities/FramerAnimations';
import CircleButton from '../CircleButton/CircleButton';
import UnreadCount from '../Communication/UnreadCount/UnreadCount';
import { DreamModalContext } from '../DreamModal/DreamModal';
import ProgressManager from '../DreamModal/ProgressManager/ProgressManager';
import DreamItems from './DreamItems';
import './ReflectionIcon.module.scss';
import { SkillModalContext } from '../SkillModal/SkillModal';
// =========================

export default function ReflectionIcon({
  name,
  image,
  onClick,
  count,
  type,
  selected,
  plusFunction,
  handleDelete,
  dream,
  preventDreamScale,
  used,
  reference,
}: {
  name: string;
  image: string;
  onClick?: () => void;
  count?: number;
  type?: 'skill' | 'small skill' | 'big skill';
  selected?: boolean;
  plusFunction?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  handleDelete?: () => void;
  dream?: DreamEntity;
  preventDreamScale?: boolean;
  used?: boolean;
  reference?: React.RefObject<HTMLImageElement>;
}) {
  const { originState } = useContext(DreamModalContext);
  const inDreamModal = !!originState;

  const hasDreamContent =
    !!(
      dream?.contributed_projects?.length ||
      dream?.reflections?.map((r) => r.sparks).flat().length ||
      (dream?.requirements.filter((r) => r.requirement_type !== 'App\\Skill')
        .length || 0) + (dream?.contributed_activities?.length || 0)
    ) && !preventDreamScale;

  const conditionalProps = hasDreamContent
    ? { initial: { scale: 1 }, whileTap: { scale: 0.9 } }
    : interactiveScale;

  //  only for dream condition
  const [hovering, setHovering] = useState(false);

  const { inReflection } = useContext(SkillModalContext);

  const conditionalImageProps = hasDreamContent
    ? {
        animate: { scale: hovering ? 1.9 : 1 },
        initial: { scale: 1 },
      }
    : {};

  return (
    <motion.div
      {...conditionalProps}
      styleName={`wrapper  ${type || ''}  ${used ? 'used' : ''}`}
      onClick={
        onClick
          ? () => {
              setHovering(false);
              onClick();
            }
          : undefined
      }
      onHoverStart={() => (hasDreamContent ? setHovering(true) : undefined)}
      onHoverEnd={() => (hasDreamContent ? setHovering(false) : undefined)}
    >
      <div styleName="relative">
        <motion.img
          {...conditionalImageProps}
          src={image}
          alt={name}
          ref={reference}
        />
        {!!dream && <DreamItems hovering={hovering} dream={dream} />}
      </div>
      <motion.p
        animate={{ y: hovering ? 40 : 0 }}
        transition={{
          type: 'spring',
          stiffness: 500,
          damping: 40,
        }}
        initial={{ y: 0 }}
      >
        {name}
      </motion.p>
      <div styleName={`count ${inReflection ? '' : 'project'}`}>
        {!!count && <UnreadCount count={count} />}
      </div>
      <AnimatePresence>
        {selected && <motion.div {...framerFade} styleName="selected" />}
      </AnimatePresence>
      {!!handleDelete && (
        <div
          styleName="delete"
          onClick={(e) => {
            e.stopPropagation();
            handleDelete();
          }}
        >
          <CircleButton
            icon={faTrash}
            color={inDreamModal ? 'white-blue-shadow' : 'white-blue'}
          />
        </div>
      )}
      {!!plusFunction && (
        <div styleName="plus" onClick={(e) => plusFunction(e)}>
          <CircleButton icon={faPlus} color="white-grey" />
        </div>
      )}
      <ProgressManager />
    </motion.div>
  );
}
